import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FeaturedSchedule } from "../components/FeaturedSchedule";
import { Star, Trophy, TrendingUp, Gift, Users, Calendar, CheckCircle2, Clock } from "lucide-react";

export default function FeaturedRewardsModule() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [stats, setStats] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get(`/featured-rewards/stats?month=${month}`),
        api.get(`/featured-rewards?month=${month}&limit=200`),
      ]);
      setStats(s.data);
      setDetails(d.data.rewards || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
    }
    setLoading(false);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const honor = async (id) => {
    try {
      await api.patch(`/featured-rewards/${id}/honor`);
      toast.success("Récompense marquée comme honorée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="featured-rewards-module">
      <FeaturedSchedule />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-amber-300 flex items-center gap-2">
            <Trophy className="w-6 h-6" /> Ambassadeurs du Mois
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Suivi des codes promo "Étudiant du mois" — ventes générées, récompenses dues.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-zinc-500" />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-sm text-zinc-100"
            data-testid="month-picker"
          />
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpi-cards">
          <KpiCard icon={Gift} label="Codes utilisés" value={stats.total_rewards_triggered} color="text-amber-300" />
          <KpiCard icon={TrendingUp} label="CA généré" value={`${stats.total_revenue_generated_cad.toFixed(2)} $`} color="text-emerald-300" />
          <KpiCard icon={Star} label="Discount donné" value={`${stats.total_discount_given_cad.toFixed(2)} $`} color="text-blue-300" />
          <KpiCard icon={Users} label="Ambassadeurs actifs" value={stats.unique_ambassadors} color="text-pink-300" />
        </div>
      )}

      {/* Leaderboard */}
      <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 overflow-hidden mb-6" data-testid="leaderboard">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-200">🏆 Classement ambassadeurs · {stats?.month || month}</h2>
          {loading && <span className="text-xs text-zinc-500">Chargement…</span>}
        </div>
        {stats?.leaderboard?.length === 0 && (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Aucune utilisation de code promo ce mois-ci. Quand un client utilisera <span className="font-mono text-amber-400">MAELLE10</span> ou un autre code featured-student, ça apparaîtra ici.
          </div>
        )}
        {stats?.leaderboard?.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">#</th>
                <th className="text-left px-4 py-2">Étudiant</th>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-right px-4 py-2">Utilisations</th>
                <th className="text-right px-4 py-2">CA généré</th>
                <th className="text-right px-4 py-2">Discount</th>
                <th className="text-center px-4 py-2">Récompenses</th>
              </tr>
            </thead>
            <tbody>
              {stats.leaderboard.map((s, i) => (
                <tr key={s.student_name} className="border-t border-zinc-800 hover:bg-zinc-900" data-testid={`ambassador-row-${i}`}>
                  <td className="px-4 py-3">
                    {i === 0 ? <Trophy className="w-4 h-4 text-amber-400" /> : <span className="text-zinc-500">{i + 1}</span>}
                  </td>
                  <td className="px-4 py-3 font-semibold text-amber-200">{s.student_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-300">{s.promo_code}</td>
                  <td className="px-4 py-3 text-right">{s.uses_count}</td>
                  <td className="px-4 py-3 text-right text-emerald-300 font-semibold">{s.total_revenue_cad.toFixed(2)} $</td>
                  <td className="px-4 py-3 text-right text-zinc-400">{s.total_discount_cad.toFixed(2)} $</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2 text-xs">
                      <span className="text-emerald-400" title="Honorées">✓ {s.honored_count}</span>
                      <span className="text-amber-400" title="En attente">⏳ {s.pending_count}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Détails — historique complet */}
      <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 overflow-hidden" data-testid="rewards-detail">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h2 className="text-sm font-semibold text-zinc-200">📋 Historique récompenses ({details.length})</h2>
        </div>
        {details.length === 0 && (
          <div className="p-8 text-center text-zinc-500 text-sm">Aucune récompense pour ce mois.</div>
        )}
        {details.length > 0 && (
          <div className="max-h-[500px] overflow-y-auto">
            {details.map((r) => (
              <div key={r.id} className="px-4 py-3 border-t border-zinc-800 flex items-center gap-3" data-testid={`reward-${r.id}`}>
                <div className="shrink-0">
                  {r.status === "honored" ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <Clock className="w-5 h-5 text-amber-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className="text-amber-200 font-semibold">{r.student_name}</span>
                    <span className="text-zinc-500"> · code </span>
                    <span className="font-mono text-xs text-amber-400">{r.promo_code}</span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    Client : {r.customer_email} · Commande {r.order_total_cad?.toFixed(2)} $ · Discount {r.discount_cad?.toFixed(2)} $
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    Récompense : {r.reward_label} · {new Date(r.created_at).toLocaleString("fr-CA")}
                  </div>
                </div>
                {r.status !== "honored" && (
                  <button
                    onClick={() => honor(r.id)}
                    className="px-3 py-1.5 rounded text-xs bg-amber-700 hover:bg-amber-600 text-amber-50 font-semibold"
                    data-testid={`honor-${r.id}`}
                  >
                    Marquer honorée
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tip footer */}
      <div className="mt-6 p-4 rounded-lg bg-amber-950/30 border border-amber-800/40 text-xs text-amber-200">
        💡 <b>Astuce</b> : À la fin du mois, partage le top ambassadeur en Story Instagram/Facebook avec leur photo et le total généré.
        Ex: <i>"Top ambassadeur Maelle a généré 327$ ce mois — merci !"</i> Ça boucle la fierté → fidélisation → bouche-à-oreille.
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-4">
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
